import React, { useEffect, useState } from 'react';
import { Zap, TrendingUp } from 'lucide-react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';
import { projectPayWeek, currentPayWeekStart } from '../../shared/growth';
import { appointmentService } from '../../services/appointmentService';
import { loadFreshaHistory, sumDaysRange } from '../../services/freshaHistoryService';

/**
 * "This Pay Week" — live commission-cliff projection (the weekly twin of the occupancy projection).
 * Shows where the barber is THIS pay week + the motivator: "$X more in services crosses the next tier
 * and your WHOLE check jumps to N% = +$Y." Cliff model → crossing the threshold lifts the entire check.
 * Null-safe (needs payWeekRevenue, written on Growth recompute). BEHAVIOR-PIN: sliding-scale pay-week
 * projection (Jose 2026-08-24) · engine projectPayWeek shared/growth.ts.
 */
const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const pct = (r: number) => `${Math.round(r * 100)}%`;

export const GrowthPayWeekCard: React.FC<{ growth?: BarberGrowthSnapshot; barberId?: string; commissionMode?: 'sliding' | 'flat'; commissionRate?: number }> = ({ growth, barberId, commissionMode, commissionRate }) => {
  // Compute THIS pay week's service revenue LIVE — the snapshot's payWeekRevenue is only refreshed by a
  // manual recompute (it read $0 for 15+ days while real sales existed). BEHAVIOR-PIN: plausible-but-wrong audit.
  const [liveRev, setLiveRev] = useState<number | null>(null);
  // Tips this pay week (Jose 2026-09-29): the card showed Services/Rate/Check but no tips — barbers
  // couldn't see their tips on this card. Sum the canonical `tipAmount` over the SAME appts as revenue
  // (matches reportingDataService's tip sum). Shown as its own line + folded into a Take-home total.
  const [liveTips, setLiveTips] = useState<number>(0);
  const [curStart, setCurStart] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (!barberId) { setLiveRev(null); setLiveTips(0); return; }
    const startDay = growth?.payWeekStart ? new Date(growth.payWeekStart + 'T00:00:00').getDay() : 3; // weekday of the pay-week grid
    const startISO = currentPayWeekStart(new Date(), startDay).toISOString().slice(0, 10);
    const todayISO = new Date().toISOString().slice(0, 10);
    setCurStart(startISO);
    const appts = appointmentService.getAppointmentsByBarber(barberId) || [];
    const paid = appts.filter((a: any) => (a.status === 'completed' || a.status === 'paid') && (a.date || '') >= startISO);
    // BLEND (Jose 2026-10-18): reconciled Fresha (which OWNS tips — appointments carry none for imported
    // visits) for its covered days + live SLIK appts on uncovered days. Matches payroll + the profile's
    // pay-week. Without this the card showed Tips $0 and under-counted services (landing in a lower tier).
    loadFreshaHistory().then(list => {
      if (!alive) return;
      const fb = list.find((x: any) => x.barberId === barberId);
      let svc = 0, tp = 0; const covered = new Set<string>();
      if (fb) {
        const s = sumDaysRange(fb, startISO, todayISO);
        svc = s.svcNet; tp = s.tips;
        for (const iso of Object.keys(fb.days || {})) if (iso >= startISO && iso <= todayISO) covered.add(iso);
      }
      const uncovered = paid.filter((a: any) => !covered.has(a.date));
      const r = svc + uncovered.reduce((s: number, a: any) => s + (a.price || 0), 0);
      const t = tp + uncovered.reduce((s: number, a: any) => s + (a.tipAmount || 0), 0);
      setLiveRev(Math.round(r * 100) / 100);
      setLiveTips(Math.round(t * 100) / 100);
    }).catch(() => {
      if (!alive) return;
      const r = paid.reduce((s: number, a: any) => s + (a.price || 0), 0);
      const tp = paid.reduce((s: number, a: any) => s + (a.tipAmount || 0), 0);
      setLiveRev(Math.round(r * 100) / 100);
      setLiveTips(Math.round(tp * 100) / 100);
    });
    return () => { alive = false; };
  }, [barberId, growth?.payWeekStart]);
  const rev = liveRev != null ? liveRev : growth?.payWeekRevenue;
  const tips = liveTips;
  if (rev == null) return null;

  // Flat-rate barbers don't ride the sliding cliff — show a simple flat summary. Jose 2026-09-01.
  if (commissionMode === 'flat') {
    const rate = (commissionRate || 0) / 100;
    const take = Math.round(rev * rate);
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500 dark:text-amber-400" /> This Pay Week
        </h3>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            ['Services', money(rev)],
            ['Your rate', pct(rate)],
            ['Your check', money(take)],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-2.5 text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">{val}</div>
            </div>
          ))}
        </div>
        {/* Tips + take-home (Jose 2026-09-29): tips weren't shown on this card before. */}
        <div className="mt-2 flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Tips <b className="text-gray-900 dark:text-white">{money(tips)}</b></span>
          <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Take-home {money(take + tips)}</span>
        </div>
        <div className="mt-3 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 text-white p-3 text-sm">
          Flat commission — every service this pay week pays <b>{pct(rate)}</b>.
        </div>
      </div>
    );
  }

  const p = projectPayWeek(rev, growth?.commissionTiers); // shop-configured weekly tiers (falls back to default)
  const start = (curStart || growth?.payWeekStart) ? new Date((curStart || growth!.payWeekStart!) + 'T00:00:00') : null;
  const startLabel = start ? start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : null;

  // progress toward the next tier (0-100), based on this tier's floor → next tier's floor.
  const progress = p.nextTierAt != null
    ? Math.min(100, Math.max(0, (rev / p.nextTierAt) * 100))
    : 100;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <Zap className="h-5 w-5 text-amber-500 dark:text-amber-400" /> This Pay Week
        {startLabel && <span className="ml-auto text-xs font-normal text-gray-400">since {startLabel}</span>}
      </h3>

      {/* where they are now */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {[
          ['Services', money(p.currentRevenue)],
          ['Your rate', pct(p.currentRate)],
          ['Your check', money(p.currentTake)],
        ].map(([label, val]) => (
          <div key={label} className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-2.5 text-center">
            <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{val}</div>
          </div>
        ))}
      </div>

      {/* Tips + take-home (Jose 2026-09-29): tips weren't shown on this card before. */}
      <div className="mt-2 flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">Tips <b className="text-gray-900 dark:text-white">{money(tips)}</b></span>
        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Take-home {money(p.currentTake + tips)}</span>
      </div>

      {p.nextTierAt != null && p.nextRate != null ? (
        <>
          {/* progress to next tier */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>{pct(p.currentRate)}</span>
              <span>next: {pct(p.nextRate)} at {money(p.nextTierAt)}</span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-green-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* the cliff hook */}
          <div className="mt-3 rounded-lg bg-gradient-to-r from-amber-500 to-green-600 text-white p-3 flex items-start gap-2">
            <TrendingUp className="h-5 w-5 shrink-0 mt-0.5" />
            <p className="text-sm">
              <b>{money(p.toNextTier || 0)} more</b> in services crosses {money(p.nextTierAt)} — and your
              {' '}<b>whole check</b> jumps to <b>{pct(p.nextRate)}</b>, worth about
              {' '}<b>+{money(p.checkJump || 0)}</b> this week.
            </p>
          </div>
        </>
      ) : (
        <div className="mt-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white p-3 text-sm">
          Top tier — every cut this week is at <b>{pct(p.currentRate)}</b>. 🔥 Keep it rolling.
        </div>
      )}
    </div>
  );
};

export default GrowthPayWeekCard;
